import { FC, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SvgIcon from "@mui/material/SvgIcon";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  EmailShareButton,
} from "react-share";

interface BlogShareButtonsProps {
  url: string;
  title: string;
  description?: string;
  variant?: "horizontal" | "vertical";
  showLabels?: boolean;
}

export const BlogShareButtons: FC<BlogShareButtonsProps> = ({
  url,
  title,
  description,
  variant = "horizontal",
  showLabels = false,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const shareUrl = useMemo(() => {
    try {
      const parsedUrl = new URL(url);
      const isLocalhost =
        parsedUrl.hostname === "localhost" ||
        parsedUrl.hostname === "127.0.0.1";

      if (!isLocalhost) {
        return parsedUrl.toString();
      }

      // Social platforms cannot access localhost. Use public base URL when available.
      const publicBase = process.env.REACT_APP_BASE_URL_LANDING;
      if (!publicBase) {
        return parsedUrl.toString();
      }

      const base = publicBase.endsWith("/") ? publicBase.slice(0, -1) : publicBase;
      return `${base}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      return url;
    }
  }, [url]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const shareButtonStyle = (color: string) => ({
    color: color,
    "&:hover": {
      backgroundColor: `${color}15`,
      color: color,
    },
  });

  return (
    <Box>
      {showLabels && (
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Share this article
        </Typography>
      )}
      <Stack
        direction={variant === "vertical" ? "column" : "row"}
        spacing={1}
        alignItems={variant === "vertical" ? "flex-start" : "center"}
      >
        <Tooltip title="Share on WhatsApp">
          <WhatsappShareButton url={shareUrl} title={title}>
            <IconButton
              component="span"
              sx={shareButtonStyle("#25D366")}
              aria-label="Share on WhatsApp"
            >
              <WhatsAppIcon />
            </IconButton>
          </WhatsappShareButton>
        </Tooltip>

        <Tooltip title="Share on Facebook">
          <FacebookShareButton url={shareUrl}>
            <IconButton
              component="span"
              sx={shareButtonStyle("#1877F2")}
              aria-label="Share on Facebook"
            >
              <FacebookIcon />
            </IconButton>
          </FacebookShareButton>
        </Tooltip>

        <Tooltip title="Share via Email">
          <EmailShareButton
            url={shareUrl}
            subject={title}
            body={description ? `${description}\n${shareUrl}` : shareUrl}
          >
            <IconButton
              component="span"
              sx={shareButtonStyle("#EA4335")}
              aria-label="Share via Email"
            >
              <EmailIcon />
            </IconButton>
          </EmailShareButton>
        </Tooltip>

        <Tooltip title="Share on LinkedIn">
          <LinkedinShareButton
            url={shareUrl}
            title={title}
            summary={description}
          >
            <IconButton
              component="span"
              sx={shareButtonStyle("#0077B5")}
              aria-label="Share on LinkedIn"
            >
              <LinkedInIcon />
            </IconButton>
          </LinkedinShareButton>
        </Tooltip>

        <Tooltip title="Share on X (Twitter)">
          <TwitterShareButton url={shareUrl} title={title}>
            <IconButton
              component="span"
              sx={shareButtonStyle("#000000")}
              aria-label="Share on X (Twitter)"
            >
              <SvgIcon>
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </SvgIcon>
            </IconButton>
          </TwitterShareButton>
        </Tooltip>

        <Tooltip title="Copy link">
          <IconButton
            onClick={handleCopyLink}
            sx={shareButtonStyle("#666666")}
            aria-label="Copy link"
          >
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCopySuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};
